import { Injectable } from '@angular/core';

import { switchMap, map, catchError } from 'rxjs/operators';
import { of } from 'rxjs';

import { Actions, createEffect, ofType } from '@ngrx/effects';

import { MessageType, ProblemDetails } from '../../../../../core/models';
import * as fromRoot from '../../../../../core/store';

import * as fromActions from '../actions';
import * as fromModels from '../../models/generic-bulk-upsert.model';
import * as fromServices from '../../services/generic-bulk-upsert.service';
import { HttpErrorResponse } from '@angular/common/http';

@Injectable()
export class GenericBulkUpsertEffects {
  constructor(private actions$: Actions, private bulkUpsertService: fromServices.GenericBulkUpsertService) {}

  validateFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.GenericBulkUpsertActionTypes.ValidateFile),
      switchMap((action: fromActions.ValidateFile) => {
        return this.bulkUpsertService.validateFile(action.payload.file, action.payload.endpointUrl).pipe(
          map((results: fromModels.GenericValidateResponse) => {
            return new fromActions.ValidateFileSuccess(results);
          }),
          catchError((res: HttpErrorResponse) => of(new fromActions.ValidateFileFailure(res)))
        );
      })
    )
  );

  validateFileFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.GenericBulkUpsertActionTypes.ValidateFileFailure),
      switchMap((action: fromActions.ValidateFileFailure) =>
        of(
          new fromRoot.DisplayMessage({
            message: this.getErrorMessage(action.payload),
            messageType: MessageType.alert,
            toast: false
          })
        )
      )
    )
  );

  uploadFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.GenericBulkUpsertActionTypes.UploadFile),
      switchMap((action: fromActions.UploadFile) => {
        return this.bulkUpsertService.uploadFile(action.payload.file, action.payload.endpointUrl).pipe(
          map((results: fromModels.GenericUploadResponse) => {
            return new fromActions.UploadFileSuccess(results);
          }),
          catchError((res: HttpErrorResponse) => of(new fromActions.UploadFileFailure(res)))
        );
      })
    )
  );

  uploadFileSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.GenericBulkUpsertActionTypes.UploadFileSuccess),
      switchMap((action: fromActions.UploadFileSuccess) =>
        of(
          new fromRoot.DisplayMessage({
            message: `Bulk upsert completed successfully! ${action.payload.changes.length} field(s) updated.`,
            messageType: MessageType.success,
            toast: false
          })
        )
      )
    )
  );

  uploadFileFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.GenericBulkUpsertActionTypes.UploadFileFailure),
      switchMap((action: fromActions.UploadFileFailure) =>
        of(
          new fromRoot.DisplayMessage({
            message: this.getErrorMessage(action.payload),
            messageType: MessageType.alert,
            toast: false
          })
        )
      )
    )
  );

  private getErrorMessage(response: HttpErrorResponse | string): string {
    if (typeof response === 'string') return response;

    if (response.status === 0) {
      return 'Network error. Please check your internet connection.';
    }

    if (response.status === 400 && response.error) {
      try {
        if (typeof response.error === 'string') return response.error;

        const problemDetails = response.error as ProblemDetails;
        if (problemDetails) {
          const firstError = problemDetails.errors ? problemDetails.errors[Object.keys(problemDetails.errors)[0]] : null;
          if (firstError) return firstError;
          else if (problemDetails.title) return problemDetails.title;
        }
      } catch (ex) {
        console.warn(ex);
      }
    }

    return response.error ? response.error.message : response.message;
  }
}